import React, { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import LessonsSidebar from "../components/lessons/LessonsSidebar";
import MainPage from "../components/templatePage/templatePage";
import { useUserContext } from "../context/UserContext";
import { useLessonContext } from "../context/LessonContext";

export default function LessonsLayout() {
  const { state: userState } = useUserContext();
  const { ensureUserLessonsLoaded } = useLessonContext();
  const { userAttributes, loading: userLoading } = userState;
  const loadTriggeredRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Eager load userLessons on mount when auth is ready
  useEffect(() => {
    const userID = userAttributes?.sub;
    
    // Wait for auth to be ready and user to be available
    if (userLoading || !userID) {
      return;
    }

    // Single-fire guard to prevent double execution under StrictMode
    if (loadTriggeredRef.current) {
      return;
    }

    // Abort any previous load if userID changed
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadTriggeredRef.current = true;

    // Trigger the cached userLessons load for this user
    console.log(`LessonsLayout: triggering eager userLessons load for user ${userID}`);
    
    ensureUserLessonsLoaded(userID).then(() => {
      console.log(`LessonsLayout: userLessons loaded successfully for user ${userID}`);
    }).catch((error) => {
      // Don't treat AbortError as a real error
      if (error.name !== 'AbortError') {
        console.error(`LessonsLayout: error loading userLessons for user ${userID}:`, error);
      }
      // Reset the flag so we can try again if needed
      loadTriggeredRef.current = false;
    });

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [userAttributes?.sub, userLoading, ensureUserLessonsLoaded]);

  // Reset load flag when user changes
  useEffect(() => {
    loadTriggeredRef.current = false;
  }, [userAttributes?.sub]);

  const lessonsContent = (
    <Box sx={{ 
      display: "grid", 
      gridTemplateColumns: "320px 1fr", 
      height: "calc(100vh - 64px - 80px)", // Account for header and footer
      overflow: "hidden",
      gap: 2,
      p: 2
    }}>
      <Box sx={{ 
        borderRight: "1px solid rgba(0,0,0,0.12)", 
        overflowY: "auto",
        pr: 1
      }}>
        <LessonsSidebar />
      </Box>
      <Box sx={{ 
        overflowY: "auto",
        pl: 1
      }}>
        <Outlet />
      </Box>
    </Box>
  );

  return <MainPage component={lessonsContent} />;
}